import React, { useState, FC, useEffect, useCallback } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  Box,
  InputAdornment,
  makeStyles,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  AccountProfileData,
  CustomInputEvent,
  CustomKeyboardEvent
} from 'src/types';
import { Pagination } from '@material-ui/lab';
import { Search as SearchIcon } from 'react-feather';
import useResolution from 'src/hooks/useResolution';
import { debounce } from 'lodash';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';

const { actions: getPcwAccountActions } = slices.pcwUserAccounts;

interface Props {
  isVisible: boolean;
  handleClose: () => void;
  user_id?: number;
  onUserSelect: (val: AccountProfileData) => void;
}

const useStyles = makeStyles({
  table: {
    tableLayout: 'fixed',
    width: '100%'
  },
  scrollContainer: {
    Height: '100%',
    overflowY: 'auto',
    marginTop: '1rem'
  },
  tableCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  mobileColumn: {
    width: '25%'
  }
});

export const EcommUserListDialog: FC<Props> = ({
  isVisible = false,
  handleClose,
  user_id = 0,
  onUserSelect = () => {}
}) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { isMobile } = useResolution();

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState<number>();
  const [customer, setAccountProfile] = useState<
    AccountProfileData[] | undefined
  >([]);
  const [userId, setUserId] = useState<number>(0);
  const [input, setInput] = useState<string>('');

  const getUserAccountList = useCallback(
    debounce(async (page_number = 1, searchValue = '') => {
      const payload = {
        page: page_number,
        ...(searchValue && { keyword: searchValue }),
        sort: 'asc',
        limit: 10,
        sort_field: 'first_name',
        action: 'voucher_list'
      };

      const response = unwrapResult(
        await dispatch(getPcwAccountActions.getPcwUserAccount(payload))
      );

      if (response.success) {
        const users = response?.originalData?.data || [];
        setLastPage(response?.originalData?.meta?.last_page);
        setAccountProfile(users);
      }
    }, 500),
    [dispatch]
  );

  const handlePageChange = (event: React.ChangeEvent<any>, value: number) => {
    setPage(value);
    getUserAccountList(value, input);
  };

  const onToolbarInputChange = (e: CustomInputEvent) => {
    const { value } = e.target;
    setInput(value);
  };

  const onSearchCustomer = () => {
    setPage(1);
    getUserAccountList(1, input);
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchCustomer();
    }
  };

  const onDialogClose = () => {
    setInput('');
    setPage(1);
    setAccountProfile([]);
    handleClose();
  };

  useEffect(() => {
    if (isVisible) {
      setPage(1);
      setInput('');
      getUserAccountList(1, '');
    }
  }, [getUserAccountList, isVisible]);

  useEffect(() => {
    if (user_id) {
      setUserId(user_id);
    }
  }, [user_id]);

  useEffect(() => {
    return () => {
      getUserAccountList.cancel();
    };
  }, [getUserAccountList]);

  return (
    <Dialog
      open={isVisible}
      onClose={onDialogClose}
      fullScreen={isMobile}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">PCW Users</DialogTitle>
      <DialogContent
        style={{ display: 'flex', flexDirection: 'column', minHeight: '400px' }}
      >
        <TextField
          value={input}
          onChange={onToolbarInputChange}
          size={isMobile ? 'small' : 'medium'}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon fontSize="small" color="action">
                  <SearchIcon />
                </SvgIcon>
              </InputAdornment>
            )
          }}
          placeholder="Search customer"
          variant="outlined"
          onKeyPress={onKeyPress}
        />
        <div className={classes.scrollContainer}>
          {isMobile ? (
            <Box>
              {customer?.map((item: any, ind) => (
                <AlternateRowContainer
                  key={ind}
                  index={ind}
                  onClick={() => {
                    setUserId(item?.id);
                    onUserSelect(item);
                  }}
                >
                  <div style={{ padding: 10, position: 'relative' }}>
                    <TextRow
                      label="Name"
                      small
                      component={
                        <div style={{ textAlign: 'center' }}>
                          <Typography style={{ fontSize: '.7em' }}>
                            {item?.first_name} {item?.last_name}
                          </Typography>
                        </div>
                      }
                    />
                    <TextRow
                      label="Email"
                      small
                      component={
                        <div style={{ textAlign: 'center' }}>
                          <Typography style={{ fontSize: '.7em' }}>
                            {item?.email || 'N/A'}
                          </Typography>
                        </div>
                      }
                    />

                    <TextRow
                      label="Mobile No"
                      small
                      noBorderBottom
                      component={
                        <div style={{ textAlign: 'center' }}>
                          <Typography style={{ fontSize: '.7em' }}>
                            {item?.mobile_no || 'N/A'}
                          </Typography>
                        </div>
                      }
                    />
                  </div>
                </AlternateRowContainer>
              ))}
            </Box>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell className={classes.mobileColumn}>
                    Mobile No
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customer?.map((item: any) => (
                  <TableRow
                    key={item?.id}
                    onClick={() => {
                      setUserId(item?.id);
                      onUserSelect(item);
                    }}
                    hover
                    selected={userId === item?.id}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell className={classes.tableCell}>
                      {`${item?.first_name} ${item?.last_name}`}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {item?.email}
                    </TableCell>

                    <TableCell
                      className={`${classes.tableCell} ${classes.mobileColumn}`}
                    >
                      {item?.mobile_no}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <Box mt="auto" display="flex" justifyContent="flex-end" p={2}>
          <Pagination
            count={lastPage}
            page={page}
            siblingCount={isMobile ? 0 : 1}
            onChange={handlePageChange}
          />
        </Box>

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Button onClick={onDialogClose} variant="outlined" color="secondary">
            Close
          </Button>
        </div>
      </DialogContent>
      <DialogActions></DialogActions>
    </Dialog>
  );
};
